import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { VacationSegment } from '../../../hooks/useVacationBlockade';

interface TriggerHandlers {
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
}

interface Props {
  segment: VacationSegment;
  children: React.ReactElement<TriggerHandlers>;
}

const CURSOR_OFFSET = 14;

/**
 * Tooltip informativo para los 3 cartelitos de bloqueo por vacaciones (real / previo /
 * posterior). Es puramente texto — no tiene ningún elemento clickeable adentro: al seguir al
 * cursor, un link dentro del propio tooltip es prácticamente imposible de alcanzar (el cursor
 * lo empuja al moverse hacia él). La acción de click vive en el `children` (el badge, el
 * banner, o el overlay/celda gris) que ya la tiene cableada por separado — el tooltip solo le
 * avisa al usuario que ese elemento es clickeable.
 *
 * Sigue al cursor (en vez de anclarse al borde del trigger, como hace un `Tooltip` de Radix)
 * porque varios triggers son enormes — el overlay de un día entero en la vista diaria, o el de
 * una celda semanal — y anclar al borde de un trigger tan grande lo hacía aparecer lejos de
 * donde el usuario estaba mirando, a veces incluso fuera de la pantalla visible.
 */
export const VacationSegmentTooltip: React.FC<Props> = ({ segment, children }) => {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const originalHandlers = useRef<TriggerHandlers>({});
  originalHandlers.current = {
    onMouseEnter: children.props.onMouseEnter,
    onMouseMove: children.props.onMouseMove,
    onMouseLeave: children.props.onMouseLeave,
  };

  const child = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      originalHandlers.current.onMouseEnter?.(e);
      setPos({ x: e.clientX, y: e.clientY });
    },
    onMouseMove: (e: React.MouseEvent) => {
      originalHandlers.current.onMouseMove?.(e);
      setPos({ x: e.clientX, y: e.clientY });
    },
    onMouseLeave: (e: React.MouseEvent) => {
      originalHandlers.current.onMouseLeave?.(e);
      setPos(null);
    },
  });

  return (
    <>
      {child}
      {pos && createPortal(
        <div
          role="tooltip"
          data-testid="vacation-segment-tooltip"
          style={{
            position: 'fixed',
            left: pos.x + CURSOR_OFFSET,
            top: pos.y + CURSOR_OFFSET,
            zIndex: 3000,
            maxWidth: 240,
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
            padding: '0.5rem 0.75rem',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <p style={{ margin: 0, fontSize: '0.75rem', color: '#374151' }}>{segment.tooltip}</p>
          <p style={{ margin: '4px 0 0', fontSize: '0.75rem', fontWeight: 600, color: '#6D57BC' }}>
            Hacé click para ver el detalle de tus días off.
          </p>
        </div>,
        document.body,
      )}
    </>
  );
};

export default VacationSegmentTooltip;
